import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { Button } from './Button'

/**
 * Dialog bygget på Radix, som giver fokusfælde, Escape-lukning,
 * `aria-modal` og scroll-lås ud af boksen. Vi tilføjer kun udseendet.
 */
export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export interface DialogContentProps {
  title: string
  /** Beskrivelse til skærmlæsere og som synlig undertekst. */
  description?: string
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
} as const

export function DialogContent({
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-scrim backdrop-blur-sm',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          SIZES[size],
          'surface-panel max-h-[calc(100dvh-4rem)] overflow-y-auto',
          'duration-[var(--duration-dialog)] ease-[var(--ease-out-soft)]',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-lg font-semibold">{title}</DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="mt-1 text-[13px] text-text-secondary">
                {description}
              </DialogPrimitive.Description>
            )}
          </div>

          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Luk dialog">
              <X aria-hidden="true" />
            </Button>
          </DialogPrimitive.Close>
        </div>

        {children && <div className="px-5 pb-5">{children}</div>}

        {footer && (
          <div className="flex justify-end gap-2 border-t border-line-subtle px-5 py-4">
            {footer}
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  /** Bruger farven for destruktive handlinger på bekræft-knappen. */
  destructive?: boolean
  isLoading?: boolean
  onConfirm: () => void
}

/**
 * Bekræftelsesdialog til handlinger, der ikke uden videre kan fortrydes.
 * Annullér er placeret først og har fokus som standard, så et utilsigtet
 * Enter-tryk ikke udfører handlingen.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Bekræft',
  cancelLabel = 'Annullér',
  destructive = false,
  isLoading = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={title}
        description={description}
        size="sm"
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost" disabled={isLoading}>
                {cancelLabel}
              </Button>
            </DialogClose>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              onClick={onConfirm}
              isLoading={isLoading}
            >
              {confirmLabel}
            </Button>
          </>
        }
      />
    </Dialog>
  )
}
